/**
 * Atelier Vanilla — couche de mouvement.
 *
 * <av-scene> enveloppe une section av-* et pilote, pour ses descendants :
 * - [data-av-reveal]           apparition unique quand l'élément entre à l'écran (classe .is-in) ;
 * - [data-av-progress]         progression 0→1 du défilement à travers l'élément (variable --p) ;
 *                              valeur "sticky" : progression pendant que le contenu collant est fixé ;
 * - [data-av-steps]            avec data-av-progress, expose l'étape active (attribut data-active) ;
 * - [data-av-parallax="0.12"]  léger décalage vertical (variable --av-py), désactivé si mouvement réduit.
 *
 * Un seul écouteur de défilement pour toute la page ; chaque scène nettoie ses observateurs
 * quand l'éditeur de thème la remplace.
 */

const root = document.documentElement;
root.classList.add('av-ready');
if (window.__avFallback) clearTimeout(window.__avFallback);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Éléments à recalculer à chaque image tant qu'ils sont visibles. */
const live = new Set();
let frame = 0;

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {HTMLElement} el
 * @param {number} vh
 */
function updateProgress(el, vh) {
  const rect = el.getBoundingClientRect();
  let p;
  if (el.dataset.avProgress === 'sticky') {
    const travel = rect.height - vh;
    p = travel > 0 ? clamp(-rect.top / travel) : rect.top < 0 ? 1 : 0;
  } else {
    p = clamp((vh - rect.top) / (rect.height + vh));
  }
  el.style.setProperty('--p', p.toFixed(4));

  if (el.hasAttribute('data-av-steps')) {
    const count = Number(el.dataset.avSteps) || 1;
    const active = String(Math.min(count - 1, Math.floor(p * count * 0.9999)));
    if (el.dataset.active !== active) el.dataset.active = active;
  }
}

/**
 * @param {HTMLElement} el
 * @param {number} vh
 */
function updateParallax(el, vh) {
  if (reducedMotion.matches) {
    el.style.removeProperty('--av-py');
    return;
  }
  const speed = Number(el.dataset.avParallax) || 0.1;
  const rect = el.getBoundingClientRect();
  const offset = rect.top + rect.height / 2 - vh / 2;
  el.style.setProperty('--av-py', `${(-offset * speed).toFixed(1)}px`);
}

function tick() {
  frame = 0;
  const vh = window.innerHeight;
  for (const el of live) {
    if (el.hasAttribute('data-av-progress')) updateProgress(el, vh);
    if (el.hasAttribute('data-av-parallax')) updateParallax(el, vh);
  }
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(tick);
}

window.addEventListener('scroll', schedule, { passive: true });
window.addEventListener('resize', schedule, { passive: true });
reducedMotion.addEventListener('change', schedule);

class AvScene extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #revealObserver;
  /** @type {IntersectionObserver | undefined} */
  #liveObserver;
  /** @type {Set<Element>} */
  #tracked = new Set();

  connectedCallback() {
    const reveals = this.querySelectorAll('[data-av-reveal]');
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      for (const el of reveals) el.classList.add('is-in');
    } else {
      this.#revealObserver = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
      // Ce qui est déjà à l'écran au chargement apparaît tout de suite (en cascade), sans attendre le défilement.
      const vh = window.innerHeight;
      const visibleNow = [];
      for (const el of reveals) {
        const rect = el.getBoundingClientRect();
        if (rect.top < vh && rect.bottom > 0 && rect.height > 0) visibleNow.push(el);
        else this.#revealObserver.observe(el);
      }
      if (visibleNow.length) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          for (const el of visibleNow) el.classList.add('is-in');
        }));
      }
    }

    const animated = this.querySelectorAll('[data-av-progress], [data-av-parallax]');
    if (!animated.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const el of animated) this.#track(el);
      schedule();
      return;
    }

    this.#liveObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#track(entry.target);
          else live.delete(/** @type {HTMLElement} */ (entry.target));
        }
        schedule();
      },
      { rootMargin: '20% 0px 20% 0px' }
    );
    for (const el of animated) this.#liveObserver.observe(el);
    // Premier calcul immédiat : évite un état intermédiaire visible au chargement.
    for (const el of animated) {
      if (el instanceof HTMLElement && el.hasAttribute('data-av-progress')) updateProgress(el, window.innerHeight);
    }
  }

  disconnectedCallback() {
    this.#revealObserver?.disconnect();
    this.#liveObserver?.disconnect();
    for (const el of this.#tracked) live.delete(/** @type {HTMLElement} */ (el));
    this.#tracked.clear();
  }

  /** @param {Element} el */
  #track(el) {
    if (!(el instanceof HTMLElement)) return;
    live.add(el);
    this.#tracked.add(el);
  }
}

if (!customElements.get('av-scene')) customElements.define('av-scene', AvScene);
schedule();
